import {
  SettingsType,
  type PcfSettingsDto,
  type SettingInfoDto,
} from '../../../../shared/api/pipeex/pcfSettingsClient';
import { buildProfileKey } from '../profile';
import {
  assessProjectParameterRequirements,
  reconcileScopedMissingParameters,
  type MappingParameterAssessment,
  type MappingParameterScopeRequirement,
} from '../mappingReadinessFamilyExclusions';
import type { AvailableProjectParameterDescriptor } from '../../../../shared/lib/revit/projectParameterMutationPlan';
import { collectMappedParameterReferencesFromSettingsDto } from './mappedParameterReferences';
import {
  diffMappedParametersAgainstCatalog,
  type MappingProjectCatalogInput,
} from './readinessDiff';

export type MappingProfileProjectMatchStatus = 'match' | 'partial' | 'missing' | 'empty' | 'error';

export interface MappingProfileProjectMatch {
  profileKey: string;
  name: string;
  settingsType: SettingInfoDto['settingsType'];
  changedOn: string;
  status: MappingProfileProjectMatchStatus;
  referenceCount: number;
  availableCount: number;
  projectAvailableCount?: number;
  missingCount: number;
  missingParameterNames: string[];
  error: string | null;
}

function getChangedOnTime(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isCompanySettingsType(settingsType: SettingInfoDto['settingsType']): boolean {
  return settingsType === SettingsType.Company;
}

function getAvailabilityRatio(match: MappingProfileProjectMatch): number {
  return match.referenceCount > 0 ? match.availableCount / match.referenceCount : 0;
}

export function scoreMappingProfilesAgainstCatalog(
  candidates: Array<{
    profile: SettingInfoDto;
    dto: PcfSettingsDto | null;
    error?: string | null;
    scopeRequirements?: MappingParameterScopeRequirement[];
    familyScopeAssessments?: MappingParameterAssessment[];
  }>,
  catalog: MappingProjectCatalogInput,
  projectParameterDescriptors: AvailableProjectParameterDescriptor[] = []
): MappingProfileProjectMatch[] {
  return candidates
    .map((candidate) => {
      const profileKey = buildProfileKey(candidate.profile);
      if (!candidate.dto) {
        return {
          profileKey,
          name: candidate.profile.name,
          settingsType: candidate.profile.settingsType,
          changedOn: candidate.profile.changedOn,
          status: 'error',
          referenceCount: 0,
          availableCount: 0,
          missingCount: 0,
          missingParameterNames: [],
          error: candidate.error ?? 'Profile could not be inspected.',
        } satisfies MappingProfileProjectMatch;
      }

      const references = collectMappedParameterReferencesFromSettingsDto(candidate.dto);
      const combinedBaseDiff = diffMappedParametersAgainstCatalog(references, {
          projectParameterNames: catalog.projectParameterNames,
          sampledParameterNames: catalog.sampledParameterNames,
        });
      const projectOnlyBaseDiff = diffMappedParametersAgainstCatalog(references, {
          projectParameterNames: catalog.projectParameterNames,
          sampledParameterNames: [],
        });
      const requirements = candidate.scopeRequirements ?? [];
      const combinedDiff = requirements.length > 0
        ? reconcileScopedMissingParameters(
            combinedBaseDiff,
            requirements,
            [
              ...assessProjectParameterRequirements(
                requirements,
                projectParameterDescriptors,
                catalog.sampledParameterNames
              ),
              ...(candidate.familyScopeAssessments ?? []),
            ]
          )
        : combinedBaseDiff;
      const projectOnlyDiff = requirements.length > 0
        ? reconcileScopedMissingParameters(
            projectOnlyBaseDiff,
            requirements,
            [
              ...assessProjectParameterRequirements(
                requirements,
                projectParameterDescriptors,
                []
              ),
              ...(candidate.familyScopeAssessments ?? []),
            ]
          )
        : projectOnlyBaseDiff;
      const missingParameterNames = combinedDiff.missing.map((reference) => reference.name);
      const referenceCount = references.length;
      const missingCount = missingParameterNames.length;
      const availableCount = referenceCount - missingCount;
      const projectAvailableCount = referenceCount - projectOnlyDiff.missing.length;
      const status: MappingProfileProjectMatchStatus =
        referenceCount === 0
          ? 'empty'
          : missingCount === 0
            ? 'match'
            : availableCount > 0
              ? 'partial'
              : 'missing';

      return {
        profileKey,
        name: candidate.profile.name,
        settingsType: candidate.profile.settingsType,
        changedOn: candidate.profile.changedOn,
        status,
        referenceCount,
        availableCount,
        projectAvailableCount,
        missingCount,
        missingParameterNames,
        error: null,
      };
    })
    .sort((left, right) => {
      const statusOrder: Record<MappingProfileProjectMatchStatus, number> = {
        match: 0,
        partial: 1,
        missing: 2,
        empty: 3,
        error: 4,
      };

      const statusDifference = statusOrder[left.status] - statusOrder[right.status];
      if (statusDifference !== 0) {
        return statusDifference;
      }

      if (left.status === 'partial' && right.status === 'partial') {
        return (
          right.availableCount - left.availableCount ||
          getAvailabilityRatio(right) - getAvailabilityRatio(left) ||
          right.referenceCount - left.referenceCount ||
          Number(isCompanySettingsType(right.settingsType)) - Number(isCompanySettingsType(left.settingsType)) ||
          getChangedOnTime(right.changedOn) - getChangedOnTime(left.changedOn) ||
          left.name.localeCompare(right.name)
        );
      }

      return (
        right.referenceCount - left.referenceCount ||
        Number(isCompanySettingsType(right.settingsType)) - Number(isCompanySettingsType(left.settingsType)) ||
        getChangedOnTime(right.changedOn) - getChangedOnTime(left.changedOn) ||
        left.name.localeCompare(right.name)
      );
    });
}
